import { useState } from "react";
import ChatPanel from "../components/ChatPanel";
import DashboardHeader from "../components/DashboardHeader";
import DashboardSkeleton from "../components/DashboardSkeleton";
import ThisWeek from "../components/ThisWeek";
import NeedsAttention from "../components/NeedsAttention";
import { useDashboard } from "../lib/useDashboard";
import { useDismissedCards } from "../lib/useDismissedCards";
import { useAttentionPreferences } from "../lib/useAttentionPreferences";
import { useActionCards } from "../lib/useActionCards";
import { useSession } from "../lib/session";
import { useParish } from "../lib/parishContext";
import { formatGreeting } from "../lib/dashboard-summary";
import { payloadTimeZone } from "../lib/timezone";
import type { AttentionCard } from "../types/dashboard";

// The dashboard's second column is the Sub-requests list — open
// sub-requests only. The worker still emits reminder-flavor cards
// (outreach_pending, quarter_prep) and dormant chat action_cards may
// merge in client-side, but none of those are sub-requests, so they
// don't render here. The payload keeps them for other surfaces.
const SUB_REQUEST_KINDS: ReadonlySet<AttentionCard["kind"]> = new Set([
  "stale_sub_request",
  "past_48h_gap",
]);

export default function Dashboard() {
  const { data, error, loading } = useDashboard();
  const { isDismissed, dismiss } = useDismissedCards();
  const { isVisible: kindIsVisible } = useAttentionPreferences();
  // Card state lives here (one hook) so the same cards feed both the
  // embedded Ask panel and the Needs-Your-Attention merge below.
  const { cards: actionCards, loading: cardsLoading, upsertCard, applyCard, retryLine, dismissCard } =
    useActionCards();
  const session = useSession();
  const { parish } = useParish();

  // A pending action card clicked in Needs Your Attention expands the
  // Ask panel and scrolls/highlights that card (v1.1 — no /chat route).
  const [focusCardId, setFocusCardId] = useState<string | null>(null);

  // Whether the Ask panel is currently expanded (reported up by
  // ChatPanel). A pending card lives in exactly one place: the open
  // panel, or — when the panel is collapsed — Needs Your Attention.
  const [panelExpanded, setPanelExpanded] = useState(false);

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (error || !data) {
    // A parish that skipped the connect-MSP step has no schedule to
    // load — that's the expected degraded state, not a failure. The
    // ConnectMspBanner above already carries the call to action;
    // this is the calm in-page explanation of the empty dashboard.
    if (parish.msp_connected === false) {
      return (
        <main className="page">
          <DashboardHeader />
          <p className="placeholder">
            Your dashboard will fill in once Ministry Scheduler Pro is connected — use the
            banner above whenever you have your API token ready.
          </p>
        </main>
      );
    }
    return (
      <main className="page">
        <DashboardHeader />
        <p className="placeholder">Couldn't load the dashboard: {error}</p>
      </main>
    );
  }

  // Pending action cards (chat-prepared plans awaiting apply) are
  // merged in client-side — the worker's dashboard route has no auth
  // context, so they ride in via GET /api/cards instead. A pending card
  // surfaces ONLY when the Ask panel is collapsed. This merge is kept
  // intact, but action_card isn't a sub-request, so the SUB_REQUEST_KINDS
  // filter below drops it from the Sub-requests list regardless; the
  // Ask panel remains its home. Per-kind visibility is still honored via
  // useAttentionPreferences.
  const actionAttention: AttentionCard[] = panelExpanded
    ? []
    : actionCards
        .filter((c) => c.status === "pending")
        .map((c) => ({
          kind: "action_card",
          id: `action_${c.id}`,
          actionCardId: c.id,
          title: c.title,
          body:
            c.contextSentence ??
            `${c.lines.length} planned action${c.lines.length === 1 ? "" : "s"} from chat`,
        }));

  const attentionCards = [...actionAttention, ...data.needsAttention].filter(
    (c) => SUB_REQUEST_KINDS.has(c.kind) && kindIsVisible(c.kind) && !isDismissed(c.id),
  );

  // Dismissing an action card is permanent and server-side ("dismiss
  // means dismiss" — the AI never re-raises it). Everything else is
  // the per-browser localStorage hide.
  const onAttentionDismiss = (cardId: string) => {
    const action = actionAttention.find((c) => c.id === cardId);
    if (action?.actionCardId) {
      void dismissCard(action.actionCardId);
    } else {
      dismiss(cardId);
    }
  };

  const greeting = (
    <h1 className="dashboard__greeting-title">
      {formatGreeting(data.greeting, session.displayName)}
    </h1>
  );

  return (
    <main className="page page--dashboard">
      <DashboardHeader greeting={greeting} />

      <div className="dashboard__grid">
        <ThisWeek items={data.thisWeek} timeZone={payloadTimeZone(data.timezone)} />
        <NeedsAttention
          cards={attentionCards}
          onDismiss={onAttentionDismiss}
          onOpenCard={setFocusCardId}
        />
        <ChatPanel
          cards={actionCards}
          cardsLoading={cardsLoading}
          onApply={applyCard}
          onRetry={retryLine}
          onDismiss={dismissCard}
          onCardStreamed={upsertCard}
          focusCardId={focusCardId}
          onClearFocus={() => setFocusCardId(null)}
          onExpandedChange={setPanelExpanded}
        />
      </div>
    </main>
  );
}
